import React from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@shared/components/ui/breadcrumb";
import {
  Home,
  Package,
  Users,
  Settings,
  FileText,
  Truck,
  ShoppingCart,
  UserCircle,
  FileCheck,
  Receipt,
} from "lucide-react";

/**
 * Dynamic Breadcrumb Component
 *
 * How to customize breadcrumb text for routes:
 * 1. Find your route segment in the `routeConfig` object below
 * 2. Set `customText` to your desired text (this will override translation)
 * 3. Set `customText` to `null` to use the translation key instead
 * 4. You can also change the icon by updating the `icon` property
 *
 * Example:
 * "my-route": { key: "some.translation.key", icon: FileText, customText: "My Custom Title" }
 */

// Route configuration with translations and icons
// You can customize the text for each route here
const routeConfig = {
  // Main navigation routes
  "": { key: "navigation.dashboard", icon: Home, customText: null },
  dashboard: { key: "navigation.dashboard", icon: Home, customText: null },
  packages: { key: "navigation.packages", icon: Package, customText: null },
  users: { key: "navigation.users", icon: Users, customText: null },
  settings: { key: "navigation.settings", icon: Settings, customText: null },
  invoices: { key: "navigation.invoices", icon: FileText, customText: null },
  pickups: { key: "navigation.pickups", icon: Truck, customText: null },
  products: {
    key: "navigation.products",
    icon: ShoppingCart,
    customText: null,
  },

  // Profile and account routes
  profile: { key: "navigation.profile", icon: UserCircle, customText: null },
  account: { key: "banking.banking", icon: UserCircle, customText: null },
  "setup-account": {
    key: "banking.addBankInformation",
    icon: UserCircle,
    customText: "Setup Account",
  },

  // Return vouchers
  "return-vouchers": {
    key: "navigation.returnVoucher",
    icon: FileCheck,
    customText: null,
  },

  // Common actions - you can customize these texts
  view: { key: "common.view", icon: FileText, customText: "View Details" },
  edit: { key: "common.edit", icon: FileText, customText: "Edit" },
  new: { key: "common.add", icon: FileText, customText: "Add New" },
  create: { key: "common.create", icon: FileText, customText: "Add New" },
  list: { key: "navigation.viewAll", icon: FileText, customText: "List" },
  details: { key: "common.view", icon: FileText, customText: "Details" },
  import: { key: "common.import", icon: FileText, customText: "Import" },
  export: { key: "common.export", icon: FileText, customText: "Export" },

  // Banking and financial
  banking: { key: "banking.banking", icon: Receipt, customText: "Banking" },

  // Settings sub-routes
  cities: {
    key: "settings.cities",
    icon: Settings,
    customText: "Cities Management",
  },
  categories: {
    key: "navigation.categories",
    icon: Settings,
    customText: "Categories",
  },
  general: {
    key: "settings.general",
    icon: Settings,
    customText: "General Settings",
  },
  delivery: {
    key: "settings.delivery",
    icon: Settings,
    customText: "Delivery Settings",
  },
  pricing: {
    key: "settings.pricing",
    icon: Settings,
    customText: "Pricing Settings",
  },

  // Add more custom routes as needed
  // Example: "my-custom-route": { key: "some.translation.key", icon: FileText, customText: "My Custom Page" },
};

const DynamicBreadcrumb = () => {
  const location = useLocation();
  const { t } = useTranslation();

  // Split pathname and filter out empty strings
  const pathSegments = location.pathname.split("/").filter(Boolean);

  // Generate breadcrumb items
  const breadcrumbItems = React.useMemo(() => {
    const items = [];

    // Always add home/dashboard as first item
    items.push({
      href: "/",
      label: t("navigation.dashboard"),
      icon: Home,
      isHome: true,
    });

    // Build path progressively for each segment
    let currentPath = "";

    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      // Skip if it's a UUID or numeric ID (common pattern for detail pages)
      const isId = /^[a-f\d]{24}$|^\d+$/.test(segment);

      if (!isId) {
        const config = routeConfig[segment];
        if (config) {
          // Use custom text if available, otherwise use translation
          const label = config.customText || t(config.key);
          items.push({
            href: currentPath,
            label: label,
            icon: config.icon,
            isLast: index === pathSegments.length - 1,
          });
        } else {
          // Fallback for unknown segments - capitalize and clean up
          const fallbackLabel = segment
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

          items.push({
            href: currentPath,
            label: fallbackLabel,
            icon: FileText,
            isLast: index === pathSegments.length - 1,
          });
        }
      }
    });

    return items;
  }, [pathSegments, t]);

  // Don't show breadcrumb on login/register pages
  if (
    location.pathname.includes("/login") ||
    location.pathname.includes("/register") ||
    location.pathname.includes("/forgot-password") ||
    location.pathname.includes("/reset-password")
  ) {
    return null;
  }

  // Show simplified breadcrumb for single level (just dashboard)
  if (breadcrumbItems.length <= 1) {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage className="flex items-center gap-2">
              <Home className="h-4 w-4" />
              {t("navigation.dashboard")}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {breadcrumbItems.map((item, index) => {
          const Icon = item.icon;
          const isLast = index === breadcrumbItems.length - 1;

          return (
            <React.Fragment key={item.href}>
              <BreadcrumbItem>
                {isLast ? (
                  <BreadcrumbPage className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link
                      to={item.href}
                      className="flex items-center gap-2 hover:text-foreground transition-colors"
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>

              {!isLast && <BreadcrumbSeparator />}
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export default DynamicBreadcrumb;
